import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  retryLouiseQuery,
  useApiClient,
  type ApiClient,
} from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";
import { paths } from "@src/packages/openapi/schema";

/**
 * Patient side of the pharmacy concierge quote workflow
 * (`/patients/{patientId}/pharmacy-quote-requests`). One request per
 * prescription: a `draft` is created as soon as the Rx upload is processed,
 * `submit` records the consent and queues one submission per target pharmacy,
 * and ops mark each submission `submitted` / `failed` from Slack. `current`
 * is the latest non-cancelled request, or `null`.
 */

type QuoteRequestCollectionPath =
  paths["/patients/{patientId}/pharmacy-quote-requests"];
type QuoteRequestItemPath =
  paths["/patients/{patientId}/pharmacy-quote-requests/{id}"];
type QuoteRequestSubmitPath =
  paths["/patients/{patientId}/pharmacy-quote-requests/{id}/submit"];
type QuoteRequestCurrentPath =
  paths["/patients/{patientId}/pharmacy-quote-requests/current"];

export type PharmacyQuoteRequest = NonNullable<
  ResponseBody<QuoteRequestCurrentPath["get"]>["data"]
>;
export type PharmacyQuoteRequestStatus = PharmacyQuoteRequest["status"];
export type PharmacyQuoteSubmission =
  PharmacyQuoteRequest["submissions"][number];
export type CreatePharmacyQuoteRequestInput = RequestBody<
  QuoteRequestCollectionPath["post"]
>;
export type UpdatePharmacyQuoteRequestInput = RequestBody<
  QuoteRequestItemPath["patch"]
>;
export type SubmitPharmacyQuoteRequestInput = RequestBody<
  QuoteRequestSubmitPath["post"]
>;

/** Ops flip submissions from Slack; poll while the request is in their hands. */
const SUBMITTED_POLL_INTERVAL_MS = 30_000;

export function pharmacyQuoteRequestQueryKey(
  tenantId: string | undefined,
  patientId: string | undefined,
) {
  return ["pharmacy-quote-request", "current", tenantId, patientId] as const;
}

async function fetchCurrentPharmacyQuoteRequest(
  client: ApiClient,
  patientId: string,
): Promise<PharmacyQuoteRequest | null> {
  const { data, error, response } = await client.GET(
    "/patients/{patientId}/pharmacy-quote-requests/current",
    { params: { path: { patientId } } },
  );
  if (data) return data.data;
  throwApiError(
    error,
    "Failed to load the pharmacy quote request",
    "useCurrentPharmacyQuoteRequest",
    response.status,
  );
}

async function patchPharmacyQuoteRequestDraft(
  client: ApiClient,
  patientId: string,
  id: string,
  body: UpdatePharmacyQuoteRequestInput,
): Promise<PharmacyQuoteRequest> {
  const { data, error, response } = await client.PATCH(
    "/patients/{patientId}/pharmacy-quote-requests/{id}",
    { params: { path: { patientId, id } }, body },
  );
  if (data) return data.data;
  throwApiError(
    error,
    "Failed to update the pharmacy quote request",
    "useUpdatePharmacyQuoteRequestDraft",
    response.status,
  );
}

export function useCurrentPharmacyQuoteRequest() {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery<PharmacyQuoteRequest | null>({
    queryKey: pharmacyQuoteRequestQueryKey(tenantId, patientId),
    queryFn: () => fetchCurrentPharmacyQuoteRequest(client, patientId),
    enabled: !!patientId,
    retry: retryLouiseQuery,
    refetchInterval: (query) =>
      query.state.data?.status === "submitted"
        ? SUBMITTED_POLL_INTERVAL_MS
        : false,
  });
}

/**
 * Writes the request a mutation returned into the `current` cache so the
 * screen updates immediately, then refetches so any ops-side change that
 * raced the mutation is picked up too.
 */
function usePharmacyQuoteRequestCommit() {
  const { tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return (request: PharmacyQuoteRequest) => {
    const queryKey = pharmacyQuoteRequestQueryKey(tenantId, patientId);
    queryClient.setQueryData<PharmacyQuoteRequest | null>(queryKey, request);
    void queryClient.invalidateQueries({ queryKey });
  };
}

/**
 * Creates the draft for a freshly processed prescription upload. The API keeps
 * one open draft per patient (409 otherwise); in that case the existing draft
 * adopts the new prescription and snapshots instead.
 */
export function useCreatePharmacyQuoteRequestDraft() {
  const { client, patientId } = useApiClient();
  const commit = usePharmacyQuoteRequestCommit();

  return useMutation({
    mutationFn: async (
      input: CreatePharmacyQuoteRequestInput,
    ): Promise<PharmacyQuoteRequest> => {
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/pharmacy-quote-requests",
        {
          params: {
            path: { patientId },
            header: { "Idempotency-Key": createIdempotencyKey() },
          },
          body: input,
        },
      );
      if (data) return data.data;
      if (response.status === 409) {
        const current = await fetchCurrentPharmacyQuoteRequest(
          client,
          patientId,
        );
        if (current?.status === "draft") {
          return patchPharmacyQuoteRequestDraft(
            client,
            patientId,
            current.id,
            input,
          );
        }
      }
      throwApiError(
        error,
        "Failed to start the pharmacy quote request",
        "useCreatePharmacyQuoteRequestDraft",
        response.status,
      );
    },
    onSuccess: commit,
  });
}

export function useUpdatePharmacyQuoteRequestDraft() {
  const { client, patientId } = useApiClient();
  const commit = usePharmacyQuoteRequestCommit();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdatePharmacyQuoteRequestInput;
    }) => patchPharmacyQuoteRequestDraft(client, patientId, id, body),
    onSuccess: commit,
  });
}

export function useSubmitPharmacyQuoteRequest() {
  const { client, patientId } = useApiClient();
  const commit = usePharmacyQuoteRequestCommit();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: SubmitPharmacyQuoteRequestInput;
    }): Promise<PharmacyQuoteRequest> => {
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/pharmacy-quote-requests/{id}/submit",
        { params: { path: { patientId, id } }, body },
      );
      if (data) return data.data;
      throwApiError(
        error,
        "Failed to send the pharmacy quote request",
        "useSubmitPharmacyQuoteRequest",
        response.status,
      );
    },
    onSuccess: commit,
  });
}

export function useCancelPharmacyQuoteRequest() {
  const { client, patientId } = useApiClient();
  const commit = usePharmacyQuoteRequestCommit();

  return useMutation({
    mutationFn: async (id: string): Promise<PharmacyQuoteRequest> => {
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/pharmacy-quote-requests/{id}/cancel",
        { params: { path: { patientId, id } } },
      );
      if (data) return data.data;
      throwApiError(
        error,
        "Failed to cancel the pharmacy quote request",
        "useCancelPharmacyQuoteRequest",
        response.status,
      );
    },
    onSuccess: commit,
  });
}
